import { useState } from 'react';
import { ChevronDown, ChevronUp, FileText, Loader2 } from 'lucide-react';

interface CallRecordingDetailsProps {
  recordingUrl?: string | null;
  transcriptionText?: string | null;
  transcriptionStatus?: string | null;
  businessId?: string;
  /** Bubble style context: outbound bubbles are blue with light text. */
  isOutbound?: boolean;
}

/**
 * Recording playback + transcript for a call in the Messages timeline.
 * Rendered as standalone bubbles BELOW the call bubble (the call bubble is
 * tap-to-redial, so these must not live inside it). Audio streams through the
 * twilio-media-proxy edge function (Twilio recording URLs require account auth).
 */
export default function CallRecordingDetails({
  recordingUrl,
  transcriptionText,
  transcriptionStatus,
  businessId,
  isOutbound = false,
}: CallRecordingDetailsProps) {
  const [showTranscript, setShowTranscript] = useState(false);

  if (!recordingUrl && !transcriptionStatus) return null;

  let audioSrc: string | null = null;
  if (recordingUrl && businessId) {
    const mediaPath = recordingUrl.replace('https://api.twilio.com', '');
    audioSrc = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/twilio-media-proxy?path=${encodeURIComponent(
      `${mediaPath}.mp3`
    )}&businessId=${businessId}`;
  }

  const bubbleClass = `max-w-[280px] sm:max-w-xs lg:max-w-md px-3.5 py-2.5 sm:px-4 sm:py-3 rounded-2xl shadow-sm ${
    isOutbound ? 'bg-blue-500 text-white' : 'bg-white text-gray-900 border border-gray-200'
  }`;
  const mutedText = isOutbound ? 'text-blue-100' : 'text-gray-500';

  const transcriptBubble =
    transcriptionStatus === 'processing' ? (
      <div className={bubbleClass}>
        <p className={`flex items-center gap-1.5 text-[11px] ${mutedText}`}>
          <Loader2 className="h-3 w-3 animate-spin" /> Transcribing…
        </p>
      </div>
    ) : transcriptionStatus === 'failed' ? (
      <div className={bubbleClass}>
        <p className={`text-[11px] ${mutedText}`}>Transcription unavailable</p>
      </div>
    ) : transcriptionText ? (
      <div className={bubbleClass}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowTranscript((v) => !v);
          }}
          className={`flex items-center gap-1 text-[12px] font-semibold ${
            isOutbound ? 'text-blue-100 hover:text-white' : 'text-blue-600 hover:text-blue-700'
          } transition-colors`}
        >
          <FileText className="h-3.5 w-3.5" />
          {showTranscript ? 'Hide transcript' : 'View transcript'}
          {showTranscript ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
        {showTranscript ? (
          <div
            className={`mt-2 max-h-48 overflow-y-auto text-[12px] leading-relaxed whitespace-pre-wrap ${
              isOutbound ? 'text-white' : 'text-gray-700'
            }`}
          >
            {transcriptionText}
          </div>
        ) : null}
      </div>
    ) : null;

  if (!audioSrc && !transcriptBubble) return null;

  return (
    <div
      className={`mt-1.5 flex flex-col gap-1.5 ${isOutbound ? 'items-end' : 'items-start'}`}
      onClick={(e) => e.stopPropagation()}
    >
      {audioSrc ? (
        <div className={bubbleClass}>
          <audio controls preload="none" src={audioSrc} className="h-9 w-[232px] sm:w-[260px]" />
        </div>
      ) : null}
      {transcriptBubble}
    </div>
  );
}
